/**
 * Ciari Video Reviews
 *
 * Swiper owns interaction (drag physics, loop cloning, autoplay lifecycle,
 * keyboard, a11y, resize); CSS owns the visual fan; this file is the glue, plus
 * the lightbox.
 *
 * Why the split is the same one ciari-product-slider.js makes: the Figma fan
 * compresses — the second step out from centre is 1.534 strides, not 2 — and
 * Swiper's effect-creative computes `base + configured * |progress|`, which is
 * strictly linear. So Swiper keeps its normal layout at a uniform stride and
 * CSS adds scale, opacity and one correction per depth via [data-pos].
 */
(function () {
  'use strict';

  /* Deferred classic scripts run in document order, so Swiper is normally
     defined before this file executes. This guards the case where it isn't — a
     reordered include, or a section injected by the theme editor before the
     vendor script has finished. The load event only needs to fire while the
     request is still in flight; if it already ran, window.Swiper is set and we
     take the first branch. */
  class CiariVideoReviews extends window.CiariCarousel.Element {
    init() {
      if (this.swiper || !window.Swiper || !this.isConnected) return;

      this.track = this.querySelector('.vrs__track');
      if (!this.track) return;

      this.names = Array.from(this.querySelectorAll('.vrs__name'));
      this.lightbox = this.querySelector('.vrs__lightbox');
      this.stage = this.querySelector('.vrs__lightbox-stage');

      var slides = this.querySelectorAll('.vrs__slide');
      var count = slides.length;

      /* One card has nothing to fan. Mark it so the CSS drops the arrows and
         pins the card at centre. */
      if (count < 2) {
        this.dataset.static = '';
        if (slides[0]) slides[0].dataset.pos = '0';
        return;
      }

      var reduceMotion = this.prefersReducedMotion;

      /* loop clones slides, and Swiper 11 needs more of them than are visible or
         it warns and mis-positions. Five are on screen at once, so anything
         under six reviews falls back to rewind, which wraps at the ends without
         cloning. */
      var canLoop = count >= 6;

      this.mountCarousel(this.track, {
        slidesPerView: 'auto',
        centeredSlides: true,
        initialSlide: Math.floor(count / 2),
        watchSlidesProgress: true,
        spaceBetween: this.gap(),
        speed: this.motionSpeed(600),
        loop: canLoop,
        loopAdditionalSlides: canLoop ? 3 : 0,
        rewind: !canLoop,
        grabCursor: true,
        navigation: {
          prevEl: this.querySelector('.vrs__nav-btn--prev'),
          nextEl: this.querySelector('.vrs__nav-btn--next')
        },
        keyboard: { enabled: true, onlyInViewport: true },
        a11y: { enabled: true },
        autoplay: this.autoplayConfig(6000),
        on: {
          init: (swiper) => this.sync(swiper),
          progress: (swiper) => this.sync(swiper),
          /* progress fires throughout the transition, and activeIndex does not
             settle until the end of it — so the name would be reading one slide
             behind at rest without this second call. A video must also never
             outlive the card it belongs to. */
          slideChange: (swiper) => {
            this.sync(swiper);
            this.muteInlineVideos();
            this.closeLightbox();
          },
          /* --vrs-card is a clamp(), so the stride changes continuously with
             the viewport, not only at the 990px breakpoint. Recompute from the
             slide's laid-out width on every resize. */
          resize: (swiper) => {
            var gap = this.gap();
            if (gap !== swiper.params.spaceBetween) {
              swiper.params.spaceBetween = gap;
              swiper.update();
            }
          }
        }
      });

      this.onClick = this.onClick.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onClosed = this.onClosed.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onPlayerMessage = this.onPlayerMessage.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);

      if (this.lightbox) {
        /* `cancel` is Escape, and it fires synchronously before the dialog
           closes itself. `close` is the backstop for anything else that shuts
           the dialog; it is queued as a task rather than dispatched inline,
           which is exactly why teardown cannot live there alone. */
        this.lightbox.addEventListener('cancel', this.onClose);
        this.lightbox.addEventListener('close', this.onClosed);
        this.lightbox.addEventListener('click', this.onDialogClick);

        var closeBtn = this.querySelector('.vrs__lightbox-close');
        if (closeBtn) closeBtn.addEventListener('click', () => this.closeLightbox());
      }

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('message', this.onPlayerMessage);

      this.inlineVideos = Array.from(this.querySelectorAll('.vrs__inline-video'));
      this.observeInlineVideos(reduceMotion);
    }

    /* Swiper positions each slide one uniform stride apart; the fan's extra
       offset is applied in CSS. spaceBetween is therefore negative — the cards
       deliberately overlap. offsetWidth is the laid-out width, unaffected by the
       scale transform (getBoundingClientRect would include it). Reading
       --vrs-card directly is not an option: custom properties compute to their
       token stream, so getPropertyValue returns the literal "clamp(...)". */
    gap() {
      var slide = this.querySelector('.vrs__slide');
      if (!slide) return 0;

      var stride = parseFloat(getComputedStyle(this).getPropertyValue('--vrs-stride'));
      if (!stride) return 0;

      return Math.round((stride - 1) * slide.offsetWidth);
    }

    /* Swiper's slide.progress is 0 at centre, negative to the right and positive
       to the left (confirmed against effect-creative, which reads progress < 0
       as the "next" slide). Flip the sign so data-pos reads naturally: positive
       is to the right. */
    sync(swiper) {
      swiper.slides.forEach(function (slide) {
        var depth = -Math.round(slide.progress);
        slide.dataset.pos = Math.max(-3, Math.min(3, depth));
      });

      var active = swiper.slides[swiper.activeIndex];
      if (!active) return;

      /* Index off the element's own data-index, never swiper.activeIndex — with
         loop enabled the active element may be a clone, and clones shift the
         index. cloneNode carries data-index across, so this stays correct. */
      var index = active.dataset.index;
      this.names.forEach(function (name) {
        name.toggleAttribute('data-active', name.dataset.index === index);
      });
    }

    /* Clicking an off-centre card centres it; clicking the centre card opens
       it. A drag that happens to end on a card must do neither — Swiper clears
       allowClick once a drag passes its threshold. */
    onClick(event) {
      if (!this.swiper) return;

      var slide = event.target.closest('.vrs__slide');
      if (!slide || !this.swiper.allowClick) return;

      if (!slide.classList.contains('swiper-slide-active')) {
        var index = parseInt(slide.dataset.index, 10);
        if (isNaN(index)) return;
        /* slideToLoop takes the real index and resolves it against the clones
           loop mode inserts; slideTo would land on the wrong card. */
        if (this.swiper.params.loop) this.swiper.slideToLoop(index);
        else this.swiper.slideTo(index);
        return;
      }

      if (slide.dataset.inlineVideo === 'true') {
        this.toggleInlineSound(slide);
        return;
      }

      this.openLightbox(slide);
    }

    toggleInlineSound(slide) {
      var media = slide.querySelector('.vrs__inline-video');
      if (!media) return;
      if (media.tagName === 'IFRAME' && !media.hasAttribute('data-player-ready')) return;

      var enableSound = !slide.hasAttribute('data-sound-on');
      this.muteInlineVideos();

      if (!enableSound) {
        if (this.swiper.autoplay && this.dataset.autoplay === 'true') this.swiper.autoplay.start();
        return;
      }

      slide.setAttribute('data-sound-on', '');
      slide.setAttribute('aria-pressed', 'true');
      if (this.swiper.autoplay) this.swiper.autoplay.stop();

      this.setInlineMuted(media, false);
      var played = this.playInlineMedia(media);
      if (played && played.catch) {
        played.catch(() => {
          this.setInlineMuted(media, true);
          slide.removeAttribute('data-sound-on');
          slide.setAttribute('aria-pressed', 'false');
        });
      }
    }

    muteInlineVideos() {
      (this.inlineVideos || []).forEach((media) => {
        this.setInlineMuted(media, true);
        var slide = media.closest('.vrs__slide');
        if (!slide) return;
        slide.removeAttribute('data-sound-on');
        slide.setAttribute('aria-pressed', 'false');
      });
    }

    setInlineMuted(media, muted) {
      if (media.tagName === 'VIDEO') {
        media.muted = muted;
        return;
      }

      if (!media.contentWindow) return;
      if (media.dataset.inlineProvider === 'vimeo') {
        media.contentWindow.postMessage({ method: 'setMuted', value: muted }, '*');
        media.contentWindow.postMessage({ method: 'setVolume', value: muted ? 0 : 1 }, '*');
      } else if (media.dataset.inlineProvider === 'youtube') {
        media.contentWindow.postMessage(JSON.stringify({
          event: 'command',
          func: muted ? 'mute' : 'unMute',
          args: []
        }), '*');
      }
    }

    playInlineMedia(media) {
      this.hydrateInlineMedia(media);
      if (media.tagName === 'VIDEO') return media.play();

      if (!media.contentWindow) return;
      if (media.dataset.inlineProvider === 'vimeo') {
        media.contentWindow.postMessage({ method: 'play' }, '*');
      } else if (media.dataset.inlineProvider === 'youtube') {
        media.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
      }
    }

    hydrateInlineMedia(media) {
      if (media.dataset.src && !media.src) media.src = media.dataset.src;
      if (media.tagName !== 'VIDEO') return;

      const sources = Array.from(media.querySelectorAll('source[data-src]'));
      if (!sources.length) return;

      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      media.load();
    }

    pauseInlineMedia(media) {
      if (media.tagName === 'VIDEO') {
        media.pause();
        return;
      }

      if (!media.contentWindow) return;
      if (media.dataset.inlineProvider === 'vimeo') {
        media.contentWindow.postMessage({ method: 'pause' }, '*');
      } else if (media.dataset.inlineProvider === 'youtube') {
        media.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
      }
    }

    onPlayerMessage(event) {
      var media = (this.inlineVideos || []).find(function (candidate) {
        return candidate.tagName === 'IFRAME' && candidate.contentWindow === event.source;
      });
      if (!media) return;

      var payload = event.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch (error) {
          return;
        }
      }

      if (!payload || (payload.event !== 'ready' && payload.event !== 'onReady')) return;

      media.setAttribute('data-player-ready', '');
      this.setInlineMuted(media, true);
      this.playInlineMedia(media);
    }

    observeInlineVideos(reduceMotion) {
      if (!this.inlineVideos.length) return;

      if (reduceMotion || !('IntersectionObserver' in window)) {
        if (reduceMotion) this.inlineVideos.forEach((media) => this.pauseInlineMedia(media));
        return;
      }

      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          var media = entry.target;
          if (entry.isIntersecting && !document.hidden) {
            media.setAttribute('data-inline-visible', '');
            var played = this.playInlineMedia(media);
            if (played && played.catch) played.catch(function () {});
          } else {
            media.removeAttribute('data-inline-visible');
            this.setInlineMuted(media, true);
            this.pauseInlineMedia(media);
            var slide = media.closest('.vrs__slide');
            if (slide) {
              slide.removeAttribute('data-sound-on');
              slide.setAttribute('aria-pressed', 'false');
            }
          }
        });
      }, { threshold: 0.15 });

      this.inlineVideos.forEach((media) => this.videoObserver.observe(media));
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.muteInlineVideos();
        (this.inlineVideos || []).forEach((media) => this.pauseInlineMedia(media));
        return;
      }

      (this.inlineVideos || []).forEach((media) => {
        if (!media.hasAttribute('data-inline-visible')) return;
        var played = this.playInlineMedia(media);
        if (played && played.catch) played.catch(function () {});
      });
    }

    onKeyDown(event) {
      if (event.key !== 'Escape') return;
      this.muteInlineVideos();
      if (this.swiper?.autoplay && this.dataset.autoplay === 'true') this.swiper.autoplay.start();
    }

    openLightbox(slide) {
      if (!this.lightbox || !this.stage) return;

      var template = slide.querySelector('.vrs__player');
      if (!template || !template.content.children.length) return;

      this.stage.replaceChildren(template.content.cloneNode(true));
      this.opener = slide;
      this.isOpen = true;

      if (this.swiper.autoplay) this.swiper.autoplay.stop();
      this.lightbox.showModal();

      /* play() here rather than an autoplay attribute: this runs inside the
         click handler, so it is a direct user gesture and does not depend on
         the browser carrying activation over to a freshly inserted element.
         Iframes get autoplay=1 in their URL, which the same gesture covers.
         If the promise is rejected the controls are still right there. */
      var video = this.stage.querySelector('video');
      if (video) {
        var played = video.play();
        if (played && played.catch) played.catch(function () {});
      }
    }

    /* Tear down first, then close, so playback stops in the same task as the
       dialog disappearing rather than one tick later. */
    closeLightbox() {
      if (!this.lightbox || !this.lightbox.open) return;
      this.onClose();
      this.lightbox.close();
      this.restoreFocus();
    }

    /* Emptying the stage removes the video or iframe outright, which is what
       actually stops playback — pausing an iframe would mean speaking each
       provider's postMessage dialect. Guarded on isOpen so it is idempotent:
       it runs from `cancel`, from `close`, and from closeLightbox, and only the
       first of those should do any work. */
    onClose() {
      if (!this.isOpen) return;
      this.isOpen = false;

      if (this.stage) this.stage.replaceChildren();

      if (this.swiper && this.swiper.autoplay && this.dataset.autoplay === 'true') {
        this.swiper.autoplay.start();
      }
    }

    /* Deliberately not part of onClose. On the Escape path that runs from
       `cancel`, which fires while the dialog is still open — and everything
       outside an open modal is inert, so the focus call would be discarded.
       This runs once the dialog is really shut. */
    restoreFocus() {
      if (this.opener && this.opener.isConnected) this.opener.focus();
      this.opener = null;
    }

    /* The dialog's own `close` event, which is queued rather than dispatched
       inline. By the time it lands the explicit paths have already torn down —
       both calls below are no-ops then — so this only does real work when
       something outside this file closed the dialog, Escape included. */
    onClosed() {
      this.onClose();
      this.restoreFocus();
    }

    /* A modal dialog's backdrop is painted by the dialog itself, so a click on
       it lands on the dialog element rather than a child. */
    onDialogClick(event) {
      if (event.target === this.lightbox) this.closeLightbox();
    }

    /* pauseOnMouseEnter covers hover; this covers keyboard focus. Swiper's
       autoplay module already handles visibilitychange on its own. */
    onFocusIn() {
      if (this.swiper && this.swiper.autoplay) this.swiper.autoplay.pause();
    }

    onFocusOut() {
      if (this.lightbox && this.lightbox.open) return;
      if (this.swiper && this.swiper.autoplay) this.swiper.autoplay.resume();
    }

    /* Theme editor: selecting a block in the sidebar should bring its card to
       the centre. The vendor sliders all read data-swiper-slide-index off the
       slide for this. */
    onBlockSelect(event) {
      if (!this.swiper || !this.contains(event.target)) return;

      var index = parseInt(event.target.dataset.swiperSlideIndex, 10);
      if (isNaN(index)) return;

      if (this.swiper.params.loop) this.swiper.slideToLoop(index);
      else this.swiper.slideTo(index);
    }

    destroy() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('message', this.onPlayerMessage);

      if (this.videoObserver) {
        this.videoObserver.disconnect();
        this.videoObserver = null;
      }

      this.muteInlineVideos();
      (this.inlineVideos || []).forEach((media) => {
        media.removeAttribute('data-inline-visible');
        this.pauseInlineMedia(media);
      });

      if (!this.swiper) return;

      this.closeLightbox();

      this.removeEventListener('click', this.onClick);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);

      if (this.lightbox) {
        this.lightbox.removeEventListener('cancel', this.onClose);
        this.lightbox.removeEventListener('close', this.onClosed);
        this.lightbox.removeEventListener('click', this.onDialogClick);
      }

      this.destroyCarousel();
      this.inlineVideos = [];
    }
  }

  if (!customElements.get('ciari-video-reviews')) {
    customElements.define('ciari-video-reviews', CiariVideoReviews);
  }
})();
